import * as React from "react";
import { ChevronDown, ChevronUp, Inbox, Loader2 } from "lucide-react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { clsx } from "clsx";
import type { SnapshotGridField } from "@/modules/snapshots/businessReports.api";
import { Ordering } from "@/types/base";
import { SnapshotCollection } from "@/modules/snapshots/snapshotCollection.types";
import { SnapshotGridTableFixedHeader } from "./headerFixed";
import { SnapshotGridTableScrollableHeader } from "./headerScrollable";
import { SnapshotGridTableFixedRow, SnapshotGridTablePipelineFixedRow } from "./rowFixed";
import { SnapshotGridTableScrollableRow, SnapshotGridTablePipelineScrollableRow } from "./rowScrollable";
import {
  getNextSnapshotGridOrdering,
  type ColumnFilterValue,
  type ColumnFilters,
  type ColumnKind,
  type ColumnReviewInfo,
} from "@/modules/snapshots/grid/sortAndFilter";
import { getSnapshotGridLayout, type SnapshotGridColumnWidths } from "./layout";
import { getNextSnapshotSelection } from "./selection";
import type { SnapshotCheckboxChangeMeta } from "@/components/snapshots/SnapshotCheckbox";
import { applyPreferredOrder, mergeVisibleColumnOrder } from "./columnOrder";
import type { SnapshotGridRowModel } from "@/modules/snapshots/grid/rowModel";
import { ColumnFilterPopover } from "@/modules/snapshots/grid/columnFilterPopover";
import { isSnapshotGridNearEnd } from "@/modules/snapshots/grid/gridInfiniteRows";
import { FamilySourceRows } from "./FamilySourceRows";

const LOAD_MORE_SCROLL_THRESHOLD = 1200;

interface OrdinaryProps {
  rows: SnapshotGridRowModel[];
  fields?: SnapshotGridField[];
  currentOrdering: Ordering;
  setOrdering: (ordering: Ordering) => void;
  snapshotCollection: SnapshotCollection;
  businessReportOutcomeResultWithEvent: ReadonlySet<string>;
  selectedSnapshots?: string[];
  onSelectedSnapshotsChange?: (ids: string[]) => void;
  onSelectAll?: (checked: boolean) => void;
  onColumnAnalysisClick?: (header: string) => void;
  onReviewColumnClick?: (header: string) => void;
  columnReview?: Record<string, ColumnReviewInfo>;
  onRerunColumnClick?: (header: string) => void;
  rerunnableColumns?: ReadonlySet<string>;
  hiddenColumns?: string[];
  columnOrder?: string[];
  onColumnOrderChange?: (newOrder: string[]) => void;
  noteColumnOrder?: string[];
  onNoteColumnOrderChange?: (newOrder: string[]) => void;
  stableColumnOrder?: string[];
  onStableColumnOrderChange?: (newOrder: string[]) => void;
  columnFilters?: ColumnFilters;
  columnTypes?: Record<string, ColumnKind>;
  onColumnFilterChange?: (columnName: string, value: ColumnFilterValue | undefined) => void;
  onNoteColumnDeleted?: (columnName: string) => void;
  columnWidths?: SnapshotGridColumnWidths;
  onColumnResize?: (columnId: string, width: number) => void;
  loadMoreRef?: React.Ref<HTMLDivElement>;
  tableScrollRef?: React.Ref<HTMLDivElement>;
  isLoadingMore?: boolean;
  hasMoreRows?: boolean;
  showLoadMoreStatus?: boolean;
  onLoadMore?: () => void;
  interactionDisabled?: boolean;
  onOpenOutcome?: (outcomeId: string) => void;
  readOnly?: boolean;
  showSelectionColumn?: boolean;
  showActionsColumn?: boolean;
  selectedGridRowId?: string | null;
  onSelectedGridRowChange?: (id: string | null) => void;
}

interface PipelineProps {
  rows: SnapshotGridRowModel[];
  fields: SnapshotGridField[];
  sourceCollectionId: string;
  templateId: string;
  currentOrdering: Ordering;
  setOrdering: (ordering: Ordering) => void;
  selectedGridRowId?: string | null;
  onSelectedGridRowChange?: (id: string | null) => void;
  onOpenCell?: (row: SnapshotGridRowModel, cell: SnapshotGridRowModel["cells"][number] | null) => void;
  columnFilters?: ColumnFilters;
  onColumnFilterChange?: (columnName: string, value: ColumnFilterValue | undefined) => void;
  emptyMessage?: string;
  ownerNamesById?: ReadonlyMap<string, string>;
}

const PipelineSnapshotGridTable: React.FC<PipelineProps> = ({
  rows,
  fields,
  sourceCollectionId,
  templateId,
  currentOrdering,
  setOrdering,
  selectedGridRowId,
  onSelectedGridRowChange,
  onOpenCell,
  columnFilters,
  onColumnFilterChange,
  emptyMessage = "No family summaries are available.",
  ownerNamesById = new Map(),
}) => {
  const visibleFields = React.useMemo(
    () => fields.filter(field => !field.is_note).sort((left, right) => left.order - right.order),
    [fields],
  );
  const columnTypes = React.useMemo(
    () => Object.fromEntries(visibleFields.map(field => [field.key, field.kind])),
    [visibleFields],
  );

  const handleOrderingChange = (field: string) => setOrdering(getNextSnapshotGridOrdering(currentOrdering, field));
  const handleSortKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, field: string) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleOrderingChange(field);
  };
  const getAriaSort = (field: string) =>
    currentOrdering.order_by !== field ? "none" : currentOrdering.descending ? "descending" : "ascending";

  return (
    <div className="min-h-0 w-full flex-grow overflow-auto">
      <table
        className="table-fixed border-separate border-spacing-0"
        data-source-collection-id={sourceCollectionId}
        data-template-id={templateId}
      >
        <thead className="bg-[#f1edea] text-black">
          <tr>
            <th
              scope="col"
              className="sticky left-0 top-0 z-40 w-10 min-w-[40px] max-w-[40px] bg-[#f1edea] py-3.5 pl-3 pr-1 text-center text-sm font-semibold text-gray-400"
            >
              #
            </th>
            <th
              scope="col"
              aria-sort={getAriaSort("system:name")}
              className="sticky left-10 top-0 z-40 w-[250px] min-w-[250px] max-w-[250px] border-r border-gray-300 bg-[#f1edea] py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
            >
              <button
                type="button"
                className="flex w-full items-center text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral"
                onClick={() => handleOrderingChange("system:name")}
                onKeyDown={event => handleSortKeyDown(event, "system:name")}
                aria-label="Sort by Document name"
              >
                Document name
                {currentOrdering.order_by === "system:name" && currentOrdering.descending ? (
                  <ChevronDown className="ml-1 h-4 w-4" />
                ) : (
                  <ChevronUp
                    className={clsx("ml-1 h-4 w-4", currentOrdering.order_by === "system:name" ? "" : "text-gray-400")}
                  />
                )}
              </button>
            </th>
            <th scope="col" className="border-b border-gray-300 px-4 py-3 text-left text-sm font-semibold">
              Supplier
            </th>
            <th scope="col" className="border-b border-gray-300 px-4 py-3 text-left text-sm font-semibold">
              Contract Family owner
            </th>
            <th scope="col" className="border-b border-gray-300 px-4 py-3 text-left text-sm font-semibold">
              Type
            </th>
            {visibleFields.map(field => (
              <th
                key={field.key}
                scope="col"
                aria-sort={getAriaSort(field.key)}
                className="border-b border-gray-300 px-4 py-3 text-left text-sm font-semibold"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <button
                    type="button"
                    className="min-w-0 truncate text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral"
                    onClick={() => handleOrderingChange(field.key)}
                    onKeyDown={event => handleSortKeyDown(event, field.key)}
                    aria-label={`Sort by ${field.name}`}
                  >
                    {field.name}
                  </button>
                  {onColumnFilterChange ? (
                    <ColumnFilterPopover
                      columnName={field.key}
                      columnKind={columnTypes[field.key] ?? "text"}
                      value={columnFilters?.[field.key]}
                      onChange={onColumnFilterChange}
                    />
                  ) : null}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white">
          {rows.length === 0 ? (
            <tr>
              <td colSpan={visibleFields.length + 2} className="px-6 py-10 text-center text-sm text-slate-600">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr
                key={row.id}
                className={clsx(
                  "group h-[45px] border-b",
                  row.presentation.kind === "family_summary"
                    ? "border-blue-200 bg-blue-50"
                    : "border-gray-200 bg-white",
                  row.presentation.kind !== "family_summary" && selectedGridRowId === row.id && "bg-blue-100",
                )}
                data-family-id={row.presentation.familyId}
                data-row-kind={row.presentation.kind}
                aria-label={`${row.presentation.kind === "family_summary" ? "Summary " : ""}${row.name}`}
              >
                <SnapshotGridTablePipelineFixedRow
                  row={row}
                  rowNumber={index + 1}
                  isHighlighted={selectedGridRowId === row.id}
                  onSelectRow={onSelectedGridRowChange}
                  ownerName={
                    row.presentation.contractFamilyOwnerUserId
                      ? ownerNamesById.get(row.presentation.contractFamilyOwnerUserId)
                      : undefined
                  }
                />
                <SnapshotGridTablePipelineScrollableRow row={row} fields={visibleFields} onOpenCell={onOpenCell} />
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

const SnapshotGridTableOrdinary: React.FC<OrdinaryProps> = ({
  rows,
  fields = [],
  currentOrdering,
  setOrdering,
  snapshotCollection,
  businessReportOutcomeResultWithEvent,
  selectedSnapshots = [],
  onSelectedSnapshotsChange,
  onSelectAll,
  onColumnAnalysisClick,
  onReviewColumnClick,
  columnReview,
  onRerunColumnClick,
  rerunnableColumns,
  hiddenColumns = [],
  columnOrder = [],
  onColumnOrderChange,
  noteColumnOrder = [],
  onNoteColumnOrderChange,
  stableColumnOrder,
  onStableColumnOrderChange,
  columnFilters,
  columnTypes,
  onColumnFilterChange,
  onNoteColumnDeleted,
  columnWidths,
  onColumnResize,
  loadMoreRef,
  tableScrollRef,
  isLoadingMore = false,
  hasMoreRows = false,
  showLoadMoreStatus = false,
  onLoadMore,
  interactionDisabled = false,
  onOpenOutcome,
  readOnly = false,
  showSelectionColumn = true,
  showActionsColumn = true,
  selectedGridRowId,
  onSelectedGridRowChange,
}) => {
  const visibleSnapshotRows = React.useMemo(
    () => rows.flatMap(row => (row.sourceOutcome ? [row.sourceOutcome] : [])),
    [rows],
  );
  const [highlightedOutcomeId, setHighlightedOutcomeId] = React.useState<string | null>(null);
  const [expandedFamilyOutcomeId, setExpandedFamilyOutcomeId] = React.useState<string | null>(null);
  React.useEffect(() => setExpandedFamilyOutcomeId(null), [snapshotCollection.id]);
  const selectedSnapshotSet = React.useMemo(() => new Set(selectedSnapshots), [selectedSnapshots]);
  const lastSelectedSnapshotIdRef = React.useRef<string | null>(null);

  const allHeaders = React.useMemo(() => {
    if (fields.length > 0) {
      return fields
        .filter(field => !field.is_note)
        .sort((a, b) => a.order - b.order)
        .map(field => field.name);
    }

    if (visibleSnapshotRows.length && visibleSnapshotRows[0].result) {
      return [...visibleSnapshotRows[0].result]
        .filter(row => !row.is_note)
        .sort((a, b) => a.order - b.order)
        .map(row => row.name);
    }
    return [];
  }, [fields, visibleSnapshotRows]);

  const allNoteHeaders = React.useMemo(() => {
    if (fields.length > 0) {
      return fields
        .filter(field => field.is_note)
        .sort((a, b) => a.order - b.order)
        .map(field => field.name)
        .filter(name => !hiddenColumns.includes(name));
    }

    const names = new Set<string>();
    for (const outcome of visibleSnapshotRows) {
      for (const r of outcome.result) {
        if (r.is_note) names.add(r.name);
      }
    }
    return [...names].filter(name => !hiddenColumns.includes(name));
  }, [fields, hiddenColumns, visibleSnapshotRows]);

  const noteHeaders = React.useMemo(
    () => applyPreferredOrder(allNoteHeaders, noteColumnOrder),
    [allNoteHeaders, noteColumnOrder],
  );

  const headers = React.useMemo(
    () =>
      applyPreferredOrder(
        allHeaders.filter(h => !hiddenColumns.includes(h)),
        columnOrder,
      ),
    [allHeaders, hiddenColumns, columnOrder],
  );

  const headerPrompts = React.useMemo(() => {
    if (fields.length > 0) {
      return Object.fromEntries(
        fields.filter(field => !field.is_note).map(field => [field.name, field.prompt ?? null]),
      );
    }

    if (visibleSnapshotRows.length && visibleSnapshotRows[0].result) {
      const map: Record<string, string | null> = {};
      for (const row of visibleSnapshotRows[0].result) {
        if (!row.is_note) {
          map[row.name] = row.prompt;
        }
      }
      return map;
    }
    return {};
  }, [fields, visibleSnapshotRows]);

  const dataColumns = React.useMemo(() => {
    if (!stableColumnOrder || fields.length === 0) return undefined;
    const remaining = new Map(
      fields.filter(field => !hiddenColumns.includes(field.name)).map(field => [field.key, field]),
    );
    const ordered = stableColumnOrder.flatMap(key => {
      const field = remaining.get(key);
      if (!field) return [];
      remaining.delete(key);
      return [field];
    });
    return [...ordered, ...remaining.values()];
  }, [fields, hiddenColumns, stableColumnOrder]);

  const hasAddNoteColumn = noteHeaders.length === 0 && !!visibleSnapshotRows[0]?.business_report_id;
  const scrollableColumnIds = React.useMemo(
    () => dataColumns?.map(field => field.key) ?? [...headers, ...noteHeaders.map(header => `note:${header}`)],
    [dataColumns, headers, noteHeaders],
  );
  const tableLayout = getSnapshotGridLayout({
    promptColumnCount: headers.length,
    noteColumnCount: noteHeaders.length,
    hasAddNoteColumn,
    showSelectionColumn,
    showActionsColumn,
    scrollableColumnIds,
    columnWidths,
  });
  const columnCount = tableLayout.fixedColumns.length + scrollableColumnIds.length + (hasAddNoteColumn ? 1 : 0);

  const orderedSnapshotIds = React.useMemo(
    () => visibleSnapshotRows.map(snapshot => snapshot.id),
    [visibleSnapshotRows],
  );

  const handleLocalSelectSnapshot = (id: string, checked: boolean, meta: SnapshotCheckboxChangeMeta) => {
    if (interactionDisabled) return;
    onSelectedSnapshotsChange?.(
      getNextSnapshotSelection({
        orderedIds: orderedSnapshotIds,
        selectedIds: selectedSnapshots,
        anchorId: lastSelectedSnapshotIdRef.current,
        targetId: id,
        targetChecked: checked,
        isRangeSelection: meta.shiftKey,
      }),
    );
    lastSelectedSnapshotIdRef.current = id;
  };

  const handleLocalSelectAll = (checked: boolean) => {
    if (interactionDisabled) return;
    lastSelectedSnapshotIdRef.current = null;
    onSelectAll?.(checked);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor),
  );

  const handleColumnDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      if (interactionDisabled) return;
      const { active, over } = event;

      if (!over || active.id === over.id) return;

      if (dataColumns && onStableColumnOrderChange) {
        const keys = dataColumns.map(field => field.key);
        const oldIndex = keys.indexOf(String(active.id));
        const newIndex = keys.indexOf(String(over.id));
        if (oldIndex >= 0 && newIndex >= 0) {
          const reorderedVisibleKeys = arrayMove(keys, oldIndex, newIndex);
          onStableColumnOrderChange(mergeVisibleColumnOrder(stableColumnOrder ?? [], reorderedVisibleKeys));
        }
        return;
      }

      const activeId = active.id as string;
      const overId = over.id as string;
      const isActiveNote = activeId.startsWith("note:");
      const isOverNote = overId.startsWith("note:");

      if (isActiveNote && isOverNote) {
        const activeNoteId = activeId.replace("note:", "");
        const overNoteId = overId.replace("note:", "");
        const oldIndex = noteHeaders.indexOf(activeNoteId);
        const newIndex = noteHeaders.indexOf(overNoteId);

        if (oldIndex !== -1 && newIndex !== -1) {
          onNoteColumnOrderChange?.(arrayMove(noteHeaders, oldIndex, newIndex));
        }
        return;
      }

      if (!isActiveNote && !isOverNote) {
        const oldIndex = headers.indexOf(activeId);
        const newIndex = headers.indexOf(overId);

        if (oldIndex !== -1 && newIndex !== -1) {
          onColumnOrderChange?.(arrayMove(headers, oldIndex, newIndex));
        }
      }
    },
    [
      dataColumns,
      headers,
      interactionDisabled,
      noteHeaders,
      onColumnOrderChange,
      onNoteColumnOrderChange,
      onStableColumnOrderChange,
      stableColumnOrder,
    ],
  );

  const handleOrderingChange = React.useCallback(
    (nextOrdering: Ordering) => {
      if (interactionDisabled) return;
      setOrdering(nextOrdering);
    },
    [interactionDisabled, setOrdering],
  );

  const handleScroll = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!hasMoreRows || isLoadingMore || !onLoadMore) return;
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      if (
        isSnapshotGridNearEnd({
          scrollTop,
          scrollHeight,
          clientHeight,
          threshold: LOAD_MORE_SCROLL_THRESHOLD,
        })
      ) {
        onLoadMore();
      }
    },
    [hasMoreRows, isLoadingMore, onLoadMore],
  );

  if (visibleSnapshotRows.length === 0) {
    return (
      <div className="flex min-h-[240px] w-full flex-grow flex-col items-center justify-center gap-3 bg-white px-6 py-16 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#f1edea]">
          <Inbox className="h-6 w-6 text-gray-400" />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900">No snapshots available</p>
          <p className="mt-1 text-sm text-gray-500">Snapshots will appear here once they finish processing.</p>
        </div>
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleColumnDragEnd}>
      <div
        ref={tableScrollRef}
        className="min-h-0 w-full flex-grow overflow-auto"
        aria-busy={interactionDisabled}
        onScroll={handleScroll}
      >
        <table
          className="table-fixed border-separate border-spacing-0"
          style={{ width: Math.max(tableLayout.tableWidth, tableLayout.fixedWidth) }}
        >
          <thead className="bg-[#f1edea] text-black">
            <tr>
              <SnapshotGridTableFixedHeader
                ordering={currentOrdering}
                setOrdering={handleOrderingChange}
                isAllSelected={
                  selectedSnapshots.length === visibleSnapshotRows.length && visibleSnapshotRows.length > 0
                }
                onSelectAll={handleLocalSelectAll}
                interactionDisabled={interactionDisabled}
                showSelectionColumn={showSelectionColumn}
                showActionsColumn={showActionsColumn}
                columnWidths={columnWidths}
                onColumnResize={onColumnResize}
              />
              <SnapshotGridTableScrollableHeader
                ordering={currentOrdering}
                setOrdering={handleOrderingChange}
                headers={headers}
                headerPrompts={headerPrompts}
                noteHeaders={noteHeaders}
                dataColumns={dataColumns}
                onColumnAnalysisClick={interactionDisabled ? undefined : onColumnAnalysisClick}
                onReviewColumnClick={interactionDisabled ? undefined : onReviewColumnClick}
                columnReview={columnReview}
                onRerunColumnClick={interactionDisabled ? undefined : onRerunColumnClick}
                rerunnableColumns={rerunnableColumns}
                snapshotCollection={snapshotCollection}
                businessReportId={visibleSnapshotRows[0]?.business_report_id}
                columnFilters={columnFilters}
                columnTypes={columnTypes}
                onColumnFilterChange={onColumnFilterChange}
                onNoteColumnDeleted={readOnly ? undefined : onNoteColumnDeleted}
                columnWidths={columnWidths}
                onColumnResize={onColumnResize}
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {visibleSnapshotRows.map((businessReportOutcome, index) => {
              const isHighlighted =
                highlightedOutcomeId === businessReportOutcome.id || selectedGridRowId === businessReportOutcome.id;

              const canExpand = Boolean(
                snapshotCollection.is_family_collection &&
                businessReportOutcome.summary_row_kind === "contract_summary",
              );
              const isExpanded = expandedFamilyOutcomeId === businessReportOutcome.id;

              return (
                <React.Fragment key={businessReportOutcome.id}>
                  <tr
                    className={clsx("group h-[53px] transition-colors", isHighlighted && "bg-blue-100")}
                    data-row-kind={businessReportOutcome.summary_row_kind ?? "snapshot"}
                    onClick={() => {
                      setHighlightedOutcomeId(businessReportOutcome.id);
                      onSelectedGridRowChange?.(businessReportOutcome.id);
                    }}
                  >
                    <SnapshotGridTableFixedRow
                      businessReportOutcome={businessReportOutcome}
                      rowNumber={index + 1}
                      isSelected={!interactionDisabled && selectedSnapshotSet.has(businessReportOutcome.id)}
                      onSelectSnapshot={
                        showSelectionColumn
                          ? (checked, meta) => handleLocalSelectSnapshot(businessReportOutcome.id, checked, meta)
                          : undefined
                      }
                      isHighlighted={isHighlighted}
                      interactionDisabled={interactionDisabled}
                      onOpenOutcome={onOpenOutcome}
                      readOnly={readOnly}
                      disableSnapshotActions={canExpand}
                      showSelectionColumn={showSelectionColumn}
                      showActionsColumn={showActionsColumn}
                      columnWidths={columnWidths}
                      expanded={isExpanded}
                      onToggleSources={
                        canExpand
                          ? () =>
                              setExpandedFamilyOutcomeId(current =>
                                current === businessReportOutcome.id ? null : businessReportOutcome.id,
                              )
                          : undefined
                      }
                    />
                    <SnapshotGridTableScrollableRow
                      businessReportOutcome={businessReportOutcome}
                      snapshotCollection={snapshotCollection}
                      promptHeaders={headers}
                      noteHeaders={noteHeaders}
                      dataColumns={dataColumns}
                      businessReportOutcomeResultWithEvent={businessReportOutcomeResultWithEvent}
                      isHighlighted={isHighlighted}
                      onHighlight={setHighlightedOutcomeId}
                      hasAddNoteColumn={hasAddNoteColumn}
                      columnWidths={columnWidths}
                      interactionDisabled={interactionDisabled}
                      readOnly={readOnly}
                    />
                  </tr>
                  {canExpand && isExpanded && (
                    <FamilySourceRows
                      collectionId={snapshotCollection.id}
                      familyOutcomeId={businessReportOutcome.id}
                      snapshotCollection={snapshotCollection}
                      headers={headers}
                      noteHeaders={noteHeaders}
                      dataColumns={dataColumns}
                      hasAddNoteColumn={hasAddNoteColumn}
                      showSelectionColumn={showSelectionColumn}
                      showActionsColumn={showActionsColumn}
                      columnWidths={columnWidths}
                      colSpan={columnCount}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {showLoadMoreStatus && (
          <div
            ref={loadMoreRef}
            className="flex h-14 items-center justify-center text-sm text-gray-500"
            aria-live="polite"
          >
            {isLoadingMore ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading more snapshots
              </span>
            ) : hasMoreRows ? (
              <button type="button" className="font-medium text-coral hover:underline" onClick={onLoadMore}>
                Load more snapshots
              </button>
            ) : (
              <span>All matching snapshots loaded</span>
            )}
          </div>
        )}
      </div>
    </DndContext>
  );
};

const isPipelineProps = (props: OrdinaryProps | PipelineProps): props is PipelineProps => "sourceCollectionId" in props;

export const SnapshotGridTable: React.FC<OrdinaryProps | PipelineProps> = props =>
  isPipelineProps(props) ? <PipelineSnapshotGridTable {...props} /> : <SnapshotGridTableOrdinary {...props} />;
